import React, { useState, useEffect, useCallback } from "react";
import { useUser } from "../contexts/UserContext";
import { useAchievements } from "../contexts/AchievementContext";
import apiService from "../services/api";
import HelpDisclosure from "../components/HelpDisclosure";
import ModalPortal from "../components/ModalPortal";
import { getLocalDateString, getEuropeanDateString, getDayOfWeek, getWeekDates as getWeekDatesUtil, createLocalDate } from "../utils/dateUtils";
import { ingredientsMatch as pantryIngredientsMatch } from "../utils/pantryQuantity";
import "../styles/MealPlans.css";

const mealPlanHelp = [
  {
    question: "What is a custom meal plan?",
    answer: "A custom plan is a named plan for a date range, such as a work trip, holiday, or weekly routine."
  },
  {
    question: "How are dates used?",
    answer: "Start and end dates decide when the plan is active and help separate it from your normal weekly view."
  },
  {
    question: "Can I add meals later?",
    answer: "Yes. Create the plan first, then add meals into days and meal slots whenever you are ready."
  }
];

const MealPlans = ({ sidebarOpen }) => {
  const { user } = useUser();
  const { handleApiResponse, isShowingAchievement } = useAchievements();
  const [mealPlans, setMealPlans] = useState([]);
  const [customPlans, setCustomPlans] = useState([]);
  const [stats, setStats] = useState({
    totalMealsPlanned: 0,
    activeMealsPlanned: 0,
    totalCost: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  
  // Debug logging for currentWeek
  useEffect(() => {
    console.log('MealPlans component loaded, currentWeek:', currentWeek.toISOString().split('T')[0]);
  }, [currentWeek]);
  const [showWeeklyPlans, setShowWeeklyPlans] = useState(true);
  const [showCreatePlanForm, setShowCreatePlanForm] = useState(false);
  const [showAddMealForm, setShowAddMealForm] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedMealType, setSelectedMealType] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showMealDetails, setShowMealDetails] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [availableRecipes, setAvailableRecipes] = useState([]);
  const [pantryItems, setPantryItems] = useState([]);
  const [recipeSearchTerm, setRecipeSearchTerm] = useState('');
  const [selectedRecipeForMeal, setSelectedRecipeForMeal] = useState(null);
  const [newMealData, setNewMealData] = useState({
    mealName: '',
    description: '',
    calories: '',
    notes: ''
  });
  const [newPlanData, setNewPlanData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: ''
  });
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedPlans, setSelectedPlans] = useState([]);

  // Custom success notification function
  const showSuccess = useCallback((message) => {
    // If an achievement is being shown, delay the success message
    if (isShowingAchievement) {
      setTimeout(() => {
        showSuccess(message);
      }, 6000); // Wait 6 seconds (5 for achievement + 1 buffer)
      return;
    }
    
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  }, [isShowingAchievement]);

  // Custom error notification function
  const showError = useCallback((message) => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  }, []);

  // Calculate stats from meal plans data
  const calculateStats = useCallback((weeklyPlans, customPlans) => {
    let totalMealsPlanned = 0;
    let activeMealsPlanned = 0;
    let totalCost = 0;

    const today = new Date();
    const todayString = today.toISOString().split('T')[0];

    // Count meals from all plans
    [...weeklyPlans, ...customPlans].forEach(plan => {
      if (plan.meals && plan.meals.length > 0) {
        plan.meals.forEach(meal => {
          totalMealsPlanned++;
          
          // Active meal criteria:
          // 1. Meal date >= today (today or future) 
          // 2. Meal is not completed/crossed out
          const mealDateString = meal.date.split('T')[0];
          const isTodayOrFuture = mealDateString >= todayString;
          const isNotCompleted = !meal.isCompleted;
          
          if (isTodayOrFuture && isNotCompleted) {
            activeMealsPlanned++;
          }
        });
      }
      totalCost += parseFloat(plan.estimatedCost || 0);
    });

    return {
      totalMealsPlanned,
      activeMealsPlanned,
      totalCost: Math.round(totalCost * 100) / 100
    };
  }, []);

  // Load meal plans data
  const loadMealPlansData = useCallback(async () => {
    if (!user) {
      console.log('No user available, skipping meal plans data load');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Loading meal plans for user:', user.email, 'ID:', user.id);
      const plansResponse = await apiService.getMealPlans();
      
      console.log('Meal plans response:', plansResponse);
      
      if (!plansResponse || !plansResponse.plans) {
        console.error('Invalid plans response:', plansResponse);
        return;
      }
      
      // Separate weekly plans from custom plans
      // Weekly plans are auto-generated with names like "Week of [date]"
      const weeklyPlans = plansResponse.plans.filter(plan => {
        const isWeekly = plan.name.startsWith('Week of ');
        console.log(`Plan ${plan.id} (${plan.name}): isWeekly: ${isWeekly}, start: ${plan.weekStartDate}, end: ${plan.weekEndDate}`);
        return isWeekly;
      });
      
      const customPlans = plansResponse.plans.filter(plan => {
        const isCustom = !plan.name.startsWith('Week of ');
        console.log(`Plan ${plan.id} (${plan.name}): isCustom: ${isCustom}, start: ${plan.weekStartDate}, end: ${plan.weekEndDate}`);
        return isCustom;
      });
      
      setMealPlans(weeklyPlans);
      setCustomPlans(customPlans);
      
      // Calculate stats from the loaded data
      const calculatedStats = calculateStats(weeklyPlans, customPlans);
      setStats(calculatedStats);
      
      console.log('Successfully loaded meal plans:', { 
        totalPlans: plansResponse.plans.length,
        weeklyPlans: weeklyPlans.length, 
        customPlans: customPlans.length,
        stats: calculatedStats
      });
      
    } catch (error) {
      console.error('Failed to load meal plans data:', error);
      if (error.response?.status === 401) {
        showSuccess('Authentication failed. Please log in again.');
      } else {
        showSuccess('Failed to load meal plans data. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [user, calculateStats, showSuccess]);

  // Load data on component mount
  useEffect(() => {
    if (user) {
      loadMealPlansData();
    }
  }, [user, loadMealPlansData]);

  useEffect(() => {
    const loadRecipePlanningSources = async () => {
      if (!user) {
        setAvailableRecipes([]);
        setPantryItems([]);
        return;
      }

      try {
        const [recipesResponse, pantryResponse] = await Promise.all([
          apiService.request('/recipes'),
          apiService.getPantryItems()
        ]);

        setAvailableRecipes(Array.isArray(recipesResponse) ? recipesResponse : []);
        setPantryItems(pantryResponse?.items || []);
      } catch (error) {
        console.error('Failed to load recipes or pantry for meal planning:', error);
      }
    };

    loadRecipePlanningSources();
  }, [user]);

  // Get current week's meal plan
  const getCurrentWeekPlan = () => {
    const startOfWeek = new Date(currentWeek);
    // Adjust for Monday start (getDay() returns 0 for Sunday, 1 for Monday, etc.)
    const dayOfWeek = startOfWeek.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // If Sunday (0), go back 6 days to Monday
    startOfWeek.setDate(startOfWeek.getDate() + daysToMonday);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    // Debug logging
    console.log('Looking for plan for week:', startOfWeek.toISOString().split('T')[0], 'to', endOfWeek.toISOString().split('T')[0]);
    console.log('Available plans:', mealPlans.map(p => ({
      id: p.id,
      name: p.name,
      start: p.weekStartDate,
      end: p.weekEndDate,
      meals: p.meals ? p.meals.length : 0
    })));

    const foundPlan = mealPlans.find(plan => {
      const planStart = new Date(plan.weekStartDate);
      const planEnd = new Date(plan.weekEndDate);
      
      // Check if the current week overlaps with this plan's date range
      // Use date comparison by setting time to 00:00:00 for accurate comparison
      const startOfWeekDate = new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate());
      const endOfWeekDate = new Date(endOfWeek.getFullYear(), endOfWeek.getMonth(), endOfWeek.getDate());
      const planStartDate = new Date(planStart.getFullYear(), planStart.getMonth(), planStart.getDate());
      const planEndDate = new Date(planEnd.getFullYear(), planEnd.getMonth(), planEnd.getDate());
      
      // Check if there's any overlap between the current week and the plan's date range
      const isOverlapping = startOfWeekDate <= planEndDate && endOfWeekDate >= planStartDate;
      
      console.log(`Plan ${plan.id} (${plan.weekStartDate} to ${plan.weekEndDate}):`, {
        planStart: planStartDate.toISOString().split('T')[0],
        planEnd: planEndDate.toISOString().split('T')[0],
        startOfWeek: startOfWeekDate.toISOString().split('T')[0],
        endOfWeek: endOfWeekDate.toISOString().split('T')[0],
        isOverlapping
      });
      
      return isOverlapping;
    });

    console.log('Found plan:', foundPlan);
    return foundPlan;
  };

  // Get meals for a specific day and meal type
  const getMealsForDay = (dayOfWeek, mealType) => {
    const currentPlan = getCurrentWeekPlan();
    if (!currentPlan) {
      console.log('No current plan found for', dayOfWeek, mealType);
      return null;
    }
    
    if (!currentPlan.meals || currentPlan.meals.length === 0) {
      console.log('Current plan has no meals:', currentPlan.id, currentPlan.name);
      return null;
    }
    
    console.log('Looking for meal:', dayOfWeek, mealType, 'in plan with meals:', currentPlan.meals.length);
    console.log('Available meals:', currentPlan.meals.map(m => ({ day: m.dayOfWeek, type: m.mealType, name: m.mealName })));
    
    const foundMeal = currentPlan.meals.find(meal => 
      meal.dayOfWeek === dayOfWeek && meal.mealType === mealType
    );
    
    console.log('Found meal:', foundMeal);
    return foundMeal;
  };

  // Get week dates using utility function
  const getWeekDates = () => {
    const dates = getWeekDatesUtil(currentWeek);
    
    // Debug logging
    console.log('getWeekDates calculation:', {
      currentWeek: getLocalDateString(currentWeek),
      dates: dates.map(d => getLocalDateString(d))
    });
    
    return dates;
  };

  const weekDates = getWeekDates();
  const currentPlan = getCurrentWeekPlan();

  const getRecipePantrySummary = (recipe) => {
    const ingredients = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
    if (ingredients.length === 0 || pantryItems.length === 0) {
      return { matched: 0, total: ingredients.length };
    }

    const matched = ingredients.filter((ingredient) =>
      pantryItems.some((item) => pantryIngredientsMatch(item.name, ingredient))
    ).length;

    return { matched, total: ingredients.length };
  };

  const filteredRecipeChoices = availableRecipes
    .filter((recipe) => {
      const query = recipeSearchTerm.trim().toLowerCase();
      if (!query) return true;
      return [
        recipe.title,
        recipe.description,
        recipe.category,
        recipe.cuisine,
        ...(Array.isArray(recipe.tags) ? recipe.tags : [])
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(query));
    })
    .slice(0, 8);

  const applyRecipeToMealForm = (recipe) => {
    const totalTime = (Number(recipe.prepTime) || 0) + (Number(recipe.cookTime) || 0);
    const ingredients = Array.isArray(recipe.ingredients) ? recipe.ingredients.slice(0, 6) : [];
    const pantrySummary = getRecipePantrySummary(recipe);
    const recipeNotes = [
      `Added from recipe: ${recipe.title}`,
      totalTime > 0 ? `${totalTime} min total` : '',
      ingredients.length > 0 ? `Ingredients: ${ingredients.join(', ')}${(recipe.ingredients || []).length > ingredients.length ? '...' : ''}` : '',
      pantrySummary.total > 0 ? `Pantry match: ${pantrySummary.matched}/${pantrySummary.total}` : ''
    ].filter(Boolean).join('\n');

    setSelectedRecipeForMeal(recipe);
    setNewMealData({
      mealName: recipe.title || '',
      description: String(recipe.description || '').slice(0, 140),
      calories: recipe.calories ? String(recipe.calories) : '',
      notes: recipeNotes
    });

    if (!selectedMealType && recipe.category) {
      const category = String(recipe.category).toLowerCase();
      if (['breakfast', 'lunch', 'dinner', 'snack'].includes(category)) {
        setSelectedMealType(category);
      }
    }
  };

  // Get meals for custom plan organized by day
  const getCustomPlanMealsByDay = (plan) => {
    const mealsByDay = {};
    const startDate = new Date(plan.weekStartDate);
    const endDate = new Date(plan.weekEndDate);
    
    // Generate all days in the plan
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dayKey = currentDate.toISOString().split('T')[0];
      const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][currentDate.getDay()];
      
      mealsByDay[dayKey] = {
        date: new Date(currentDate),
        dayOfWeek: dayOfWeek,
        meals: {
          breakfast: [],
          lunch: [],
          dinner: [],
          snack: []
        }
      };
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // Organize meals by day and type (only if meals exist)
    if (plan.meals && plan.meals.length > 0) {
      plan.meals.forEach(meal => {
        const mealDate = meal.date.split('T')[0];
        if (mealsByDay[mealDate]) {
          mealsByDay[mealDate].meals[meal.mealType].push(meal);
        }
      });
    }
    
    return mealsByDay;
  };

  // Handle create custom plan
  const handleCreateCustomPlan = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const planData = {
        name: newPlanData.name,
        description: newPlanData.description.trim().slice(0, 160),
        weekStartDate: newPlanData.startDate,
        weekEndDate: newPlanData.endDate,
        status: 'active',
        estimatedCost: 0,
        totalCalories: 0
      };

      const response = await apiService.createMealPlan(planData);
      console.log('🏆 Custom plan created:', response);
      console.log('🏆 New achievements in response:', response.newAchievements);
      
      // Handle achievement notifications
      handleApiResponse(response);
      
      // Show success message with points if earned
      if (response.pointsEarned && response.pointsEarned > 0) {
        showSuccess(`Custom meal plan created successfully! +${response.pointsEarned} points earned! 🎉`);
      } else {
        showSuccess('Custom meal plan created successfully! (Points already earned for meal planning today)');
      }
      
      setShowCreatePlanForm(false);
      setNewPlanData({ name: '', description: '', startDate: '', endDate: '' });
      // Reload data to include the new plan
      await loadMealPlansData();
    } catch (error) {
      console.error('Failed to create custom plan:', error);
      showSuccess('Failed to create custom plan. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle add meal
  const handleAddMeal = async (e) => {
    e.preventDefault();
    if (!selectedPlanId) return;
    
    setIsLoading(true);
    try {
      // Check if this is a custom plan (not a weekly plan)
      const isCustomPlan = customPlans.some(plan => plan.id === selectedPlanId);
      
      // For custom plans, we need to determine the date based on the selected day
      let mealDate = getLocalDateString(); // Default to today using local time
      let dayOfWeek = 'monday'; // Default day of week
      
      if (isCustomPlan && selectedDay) {
        // For custom plans, selectedDay is a specific date string (YYYY-MM-DD)
        mealDate = selectedDay;
        // Calculate the day of week from the date using local time
        const dateObj = createLocalDate(selectedDay);
        dayOfWeek = getDayOfWeek(dateObj);
      } else if (!isCustomPlan && selectedDay) {
        // For weekly plans, calculate the date based on the day of week
        const weekDates = getWeekDates();
        const dayIndex = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].indexOf(selectedDay);
        if (dayIndex !== -1 && weekDates[dayIndex]) {
          mealDate = getLocalDateString(weekDates[dayIndex]);
          dayOfWeek = selectedDay;
          
          // Debug logging
          console.log('Weekly plan meal calculation:', {
            selectedDay,
            dayIndex,
            weekDates: weekDates.map(date => getLocalDateString(date)),
            calculatedMealDate: mealDate,
            calculatedDayOfWeek: dayOfWeek
          });
        }
      } else {
        // If no selectedDay is provided, default to today for the current week
        const today = new Date();
        mealDate = getLocalDateString(today);
        dayOfWeek = getDayOfWeek(today);
      }
      
      // CRITICAL FIX: Always ensure dayOfWeek matches the actual date
      // This prevents the bug where dayOfWeek is 'monday' but date is '2025-10-14' (Tuesday)
      const actualDateObj = createLocalDate(mealDate);
      const actualDayOfWeek = getDayOfWeek(actualDateObj);
      
      // Only update dayOfWeek if it doesn't match the actual date
      if (dayOfWeek !== actualDayOfWeek) {
        console.warn('Day of week mismatch detected and corrected:', {
          originalDayOfWeek: dayOfWeek,
          actualDayOfWeek: actualDayOfWeek,
          mealDate: mealDate
        });
        dayOfWeek = actualDayOfWeek;
      }
      
      const mealData = {
        mealName: newMealData.mealName,
        mealType: selectedMealType || 'dinner',
        dayOfWeek: dayOfWeek,
        date: mealDate,
        description: newMealData.description.trim().slice(0, 140),
        calories: parseInt(newMealData.calories) || 0,
        notes: newMealData.notes
      };
      
      // Debug logging
      console.log('Sending meal data to backend:', {
        mealData,
        isCustomPlan,
        selectedDay,
        selectedPlanId
      });

      const response = await apiService.addMealPlanMeal(selectedPlanId, mealData);
      console.log('🏆 Meal added:', response);
      console.log('🏆 New achievements in add meal response:', response.newAchievements);
      
      // Handle achievement notifications
      handleApiResponse(response);
      
      // Show success message with points if earned
      if (response.pointsEarned && response.pointsEarned > 0) {
        showSuccess(`Meal added successfully! +${response.pointsEarned} points earned! 🎉`);
        
        // Dispatch custom event to notify Dashboard
        window.dispatchEvent(new CustomEvent('activityCompleted'));
      } else {
        showSuccess('Meal added successfully! (Points already earned for meal planning today)');
      }
      
      setShowAddMealForm(false);
      setNewMealData({ mealName: '', description: '', calories: '', notes: '' });
      setRecipeSearchTerm('');
      setSelectedRecipeForMeal(null);
      setSelectedDay('');
      setSelectedMealType('');
      // Reload data to include the new meal
      await loadMealPlansData();
    } catch (error) {
      console.error('Failed to add meal:', error);
      showSuccess('Failed to add meal. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle toggle meal completion
  const handleToggleMeal = async (mealId, isCompleted) => {
    try {
      await apiService.updateMealPlanMeal(mealId, { isCompleted });
      loadMealPlansData();
    } catch (error) {
      console.error('Failed to update meal:', error);
      showSuccess('Failed to update meal. Please try again.');
    }
  };

  // Handle delete meal
  const handleDeleteMeal = async (mealId) => {
    try {
      await apiService.deleteMealPlanMeal(mealId);
      showSuccess('Meal deleted successfully!');
      loadMealPlansData();
    } catch (error) {
      console.error('Failed to delete meal:', error);
      showSuccess('Failed to delete meal. Please try again.');
    }
  };

  // Handle delete plan
  const handleDeletePlan = async () => {
    if (!planToDelete) return;
    
    setIsLoading(true);
    try {
      await apiService.deleteMealPlan(planToDelete.id);
      showSuccess('Meal plan deleted successfully!');
      setShowDeleteConfirm(false);
      setPlanToDelete(null);
      loadMealPlansData();
    } catch (error) {
      console.error('Failed to delete meal plan:', error);
      showSuccess('Failed to delete meal plan. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle bulk select toggle
  const handleBulkSelectToggle = () => {
    setBulkSelectMode(!bulkSelectMode);
    setSelectedPlans([]);
  };

  // Handle plan selection
  const handlePlanSelect = (planId) => {
    if (selectedPlans.includes(planId)) {
      setSelectedPlans(selectedPlans.filter(id => id !== planId));
    } else {
      setSelectedPlans([...selectedPlans, planId]);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedPlans.length === 0) return;
    
    setIsLoading(true);
    try {
      await apiService.bulkDeleteMealPlans(selectedPlans);
      showSuccess(`${selectedPlans.length} meal plan(s) deleted successfully!`);
      setBulkSelectMode(false);
      setSelectedPlans([]);
      loadMealPlansData();
    } catch (error) {
      console.error('Failed to delete meal plans:', error);
      showError('Failed to delete some meal plans. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading or authentication message
  if (!user) {
    return (
      <div className={`mealplans-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="mealplans-header">
          <div>
            <h1 className="mealplans-title">
              <span>📅</span>
              Meal Plans
            </h1>
            <p className="mealplans-subtitle">Plan your meals and stay organized</p>
          </div>
        </div>
        <div className="auth-required-message">
          <div className="auth-message-content">
            <div className="auth-icon">🔐</div>
            <h3>Authentication Required</h3>
            <p>Please log in to access your meal plans and create new ones.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`mealplans-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
      {/* Header */}
      <div className="mealplans-header">
        <div>
          <h1 className="mealplans-title">
            <span>📅</span>
            Meal Plans
          </h1>
          <p className="mealplans-subtitle">Plan your meals and stay organized</p>
        </div>
        <div className="header-actions">
          <button 
            className={`bulk-select-btn ${bulkSelectMode ? 'active' : ''}`}
            onClick={handleBulkSelectToggle}
            title="Select multiple plans to delete"
          >
            <span>{bulkSelectMode ? '✓' : '☐'}</span>
            {bulkSelectMode ? 'Cancel Select' : 'Bulk Select'}
          </button>
          <button 
            className="create-plan-btn"
            onClick={() => setShowCreatePlanForm(true)}
          >
            <span>+</span>
            Create Custom Plan
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="quick-stats">
        <div className="stat-card">
          <div className="stat-number green">{stats.totalMealsPlanned}</div>
          <div className="stat-label">Total Meals Planned</div>
        </div>
        <div className="stat-card">
          <div className="stat-number blue">{stats.activeMealsPlanned}</div>
          <div className="stat-label">Active Meals Planned</div>
        </div>
      </div>

      {/* Weekly Plans Section */}
      <div className="weekly-plans-section">
        <div className="section-header">
          <div className="section-title-container">
            <h2 className="section-title">Weekly Plans</h2>
            <label className="toggle-label-small">
              <input 
                type="checkbox" 
                checked={showWeeklyPlans}
                onChange={(e) => setShowWeeklyPlans(e.target.checked)}
                className="toggle-checkbox"
              />
              <span className="toggle-slider-small"></span>
              <span className="toggle-text-small">Show</span>
            </label>
          </div>
        </div>

        {showWeeklyPlans && (
          <div className="weekly-grid-container">
            {/* Week Navigation */}
            <div className="week-navigation">
              <button 
                className="week-nav-btn"
                onClick={() => {
                  const newWeek = new Date(currentWeek);
                  newWeek.setDate(currentWeek.getDate() - 7);
                  setCurrentWeek(newWeek);
                }}
              >
                ‹ Previous Week
              </button>
              <div className="current-week-display">
                {weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
              <button 
                className="week-nav-btn"
                onClick={() => {
                  const newWeek = new Date(currentWeek);
                  newWeek.setDate(currentWeek.getDate() + 7);
                  setCurrentWeek(newWeek);
                }}
              >
                Next Week ›
              </button>
            </div>

            {/* Weekly Grid */}
            <div className="weekly-grid">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((dayOfWeek, dayIndex) => {
                const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                const date = weekDates[dayIndex];
                
                // Check if this day is today - use a more reliable method
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const isToday = date.getFullYear() === today.getFullYear() && 
                               date.getMonth() === today.getMonth() && 
                               date.getDate() === today.getDate();
                
                console.log(`Day: ${dayNames[dayIndex]} ${getEuropeanDateString(date)}, Today: ${getEuropeanDateString(today)}, IsToday: ${isToday}`);
                
                return (
                  <div key={dayOfWeek} className={`day-column ${isToday ? 'today-column' : ''}`}>
                    <div className="day-header">
                      <div className="day-name">{dayNames[dayIndex]}</div>
                      <div className="day-date">{getEuropeanDateString(date)}</div>
                    </div>
                    
                    {['breakfast', 'lunch', 'dinner'].map((mealType) => {
                      const meal = getMealsForDay(dayOfWeek, mealType);
                      
                      return (
                        <div key={mealType} className="meal-slot">
                          <div className="meal-label">{mealType.charAt(0).toUpperCase() + mealType.slice(1)}</div>
                          {meal ? (
                            <div className="meal-display">
                              <div 
                                className="meal-item clickable"
                                onClick={() => {
                                  setSelectedMeal(meal);
                                  setShowMealDetails(true);
                                }}
                              >
                                <input 
                                  type="checkbox" 
                                  className="meal-checkbox"
                                  checked={meal.isCompleted}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleToggleMeal(meal.id, e.target.checked);
                                  }}
                                />
                                <div className="meal-content">
                                  <span className={`meal-name ${meal.isCompleted ? 'completed' : ''}`}>
                                    {meal.mealName}
                                  </span>
                                  {meal.calories > 0 && (
                                    <span className="meal-calories">{meal.calories} cal</span>
                                  )}
                                </div>
                                <button 
                                  className="delete-meal-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteMeal(meal.id);
                                  }}
                                  title="Delete meal"
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button 
                              className="add-meal-btn"
                              onClick={async () => {
                                if (!currentPlan) {
                                  // Auto-create a weekly plan for the current week
                                  try {
                                    // Calculate Monday of the current week
                                    const startDate = new Date(currentWeek);
                                    const dayOfWeek = startDate.getDay();
                                    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // If Sunday (0), go back 6 days to Monday
                                    startDate.setDate(startDate.getDate() + daysToMonday);
                                    
                                    const endDate = new Date(startDate);
                                    endDate.setDate(startDate.getDate() + 6);

                                    const planData = {
                                      name: `Week of ${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
                                      description: 'Weekly meal plan',
                                      weekStartDate: startDate.toISOString().split('T')[0],
                                      weekEndDate: endDate.toISOString().split('T')[0],
                                      status: 'active',
                                      estimatedCost: 0,
                                      totalCalories: 0
                                    };

                                    const response = await apiService.createMealPlan(planData);
                                    console.log('🏆 Weekly plan created:', response);
                                    console.log('🏆 New achievements in response:', response.newAchievements);
                                    
                                    // Handle achievement notifications
                                    handleApiResponse(response);
                                    
                                    setSelectedPlanId(response.plan.id);
                                    setSelectedDay(dayOfWeek);
                                    setSelectedMealType(mealType);
                                    setShowAddMealForm(true);
                                    // Reload data to include the new plan
                                    await loadMealPlansData();
                                  } catch (error) {
                                    console.error('Failed to create weekly plan:', error);
                                    showSuccess('Failed to create weekly plan. Please try again.');
                                  }
                                  return;
                                }
                                // Set the selected day and meal type for this specific slot
                                setSelectedPlanId(currentPlan.id);
                                setSelectedDay(dayOfWeek);
                                setSelectedMealType(mealType);
                                setShowAddMealForm(true);
                              }}
                            >
                              + Add meal
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {bulkSelectMode && (
        <div className="bulk-actions-bar">
          <div className="bulk-info">
            <span>{selectedPlans.length} plan(s) selected</span>
          </div>
          <div className="bulk-actions">
            <button 
              className="bulk-delete-btn"
              onClick={handleBulkDelete}
              disabled={selectedPlans.length === 0 || isLoading}
            >
              <span>🗑️</span>
              Delete Selected ({selectedPlans.length})
            </button>
          </div>
        </div>
      )}

      {/* Custom Plans Section */}
      <div className="custom-plans-section">
        <div className="section-header">
          <h2 className="section-title">Custom Plans</h2>
          <button 
            className="create-custom-btn"
            onClick={() => setShowCreatePlanForm(true)}
          >
            <span>+</span>
            Create Custom Plan
          </button>
        </div>

        {customPlans.length > 0 ? (
          <div className="custom-plans-grid">
            {customPlans.map((plan) => {
              const completedMeals = plan.meals ? plan.meals.filter(meal => meal.isCompleted).length : 0;
              const totalMeals = plan.meals ? plan.meals.length : 0;
              const mealsByDay = getCustomPlanMealsByDay(plan);
              
              return (
                <div 
                  key={plan.id} 
                  className={`custom-plan-card ${bulkSelectMode ? 'bulk-select-mode' : ''} ${selectedPlans.includes(plan.id) ? 'selected' : ''}`}
                  onClick={bulkSelectMode ? () => handlePlanSelect(plan.id) : undefined}
                  style={{ cursor: bulkSelectMode ? 'pointer' : 'default' }}
                >
                  {bulkSelectMode && (
                    <div className="bulk-select-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedPlans.includes(plan.id)}
                        onChange={() => handlePlanSelect(plan.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}
                  <div className="plan-header">
                    <h3 className="plan-title">{plan.name}</h3>
                    <div className="plan-actions">
                      <button 
                        className="action-btn"
                        onClick={() => {
                          setPlanToDelete(plan);
                          setShowDeleteConfirm(true);
                        }}
                        title="Delete Plan"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  <div className="plan-stats">
                    <div className="stat-item">
                      <span>✅</span>
                      <span>{completedMeals}/{totalMeals} completed</span>
                    </div>
                  </div>

                  {plan.description && (
                    <div className="plan-description">{plan.description}</div>
                  )}
                  
                  <div className="plan-dates">
                    <span className="date-range">
                      {new Date(plan.weekStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(plan.weekEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  
                  {/* Custom Plan Days Layout - Always Show */}
                  <div className="custom-plan-days">
                    <h4>Meal Schedule:</h4>
                    <div className="custom-days-grid">
                      {Object.entries(mealsByDay).map(([dateKey, dayData]) => (
                        <div key={dateKey} className="custom-day-column">
                          <div className="custom-day-header">
                            <div className="custom-day-name">{dayData.dayOfWeek.charAt(0).toUpperCase() + dayData.dayOfWeek.slice(1)}</div>
                            <div className="custom-day-date">{dayData.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                          </div>
                          
                          {['breakfast', 'lunch', 'dinner'].map((mealType) => {
                            const meals = dayData.meals[mealType] || [];
                            
                            return (
                              <div key={mealType} className="custom-meal-slot">
                                <div 
                                  className="custom-meal-label clickable-meal-label"
                                  onClick={() => {
                                    setSelectedPlanId(plan.id);
                                    setSelectedDay(dateKey); // Use the date key for custom plans
                                    setSelectedMealType(mealType);
                                    setShowAddMealForm(true);
                                  }}
                                  title={`Add ${mealType} meal`}
                                >
                                  {mealType.toUpperCase()}
                                </div>
                                {meals.length > 0 && (
                                  <div className="custom-meals-list">
                                    {meals.map((meal) => (
                                      <div key={meal.id} className="custom-meal-item">
                                        <input 
                                          type="checkbox" 
                                          className="meal-checkbox"
                                          checked={meal.isCompleted}
                                          onChange={(e) => handleToggleMeal(meal.id, e.target.checked)}
                                        />
                                        <span className={`meal-name ${meal.isCompleted ? 'completed' : ''}`}>
                                          {meal.mealName}
                                        </span>
                                        <button 
                                          className="delete-meal-btn"
                                          onClick={() => handleDeleteMeal(meal.id)}
                                          title="Delete meal"
                                        >
                                          🗑️
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-custom-plans">
            <div className="no-plans-icon">📅</div>
            <h4>No custom meal plans yet</h4>
            <p>Create custom meal plans for special occasions like work trips, vacations, or events!</p>
            <button 
              className="create-first-plan-btn"
              onClick={() => setShowCreatePlanForm(true)}
            >
              Create Your First Custom Plan
            </button>
          </div>
        )}
      </div>

      {/* Create Custom Plan Modal */}
      {showCreatePlanForm && (
        <div className="ingredient-modal-overlay">
          <div className="ingredient-modal-container">
            <div className="ingredient-modal-header">
              <h2 className="ingredient-modal-title">CREATE MEAL PLAN</h2>
              <div className="ingredient-modal-subheader">
                <span className="quick-kitchen">MEAL PLANNING</span>
                <HelpDisclosure title="Meal plan help" items={mealPlanHelp} />
              </div>
              <button 
                className="ingredient-modal-close" 
                onClick={() => setShowCreatePlanForm(false)}
              >
                ×
              </button>
            </div>
            <div className="ingredient-modal-content">
              <form onSubmit={handleCreateCustomPlan}>
                <div className="form-group">
                  <label>Plan Name *</label>
                  <input 
                    type="text" 
                    value={newPlanData.name}
                    onChange={(e) => setNewPlanData({...newPlanData, name: e.target.value})}
                    required 
                    className="form-input"
                    placeholder="e.g., Work Trip to NYC"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea 
                    value={newPlanData.description}
                    onChange={(e) => setNewPlanData({...newPlanData, description: e.target.value.slice(0, 160)})}
                    className="form-textarea"
                    placeholder="Optional description..."
                    maxLength="160"
                    rows="4"
                  />
                  <div className="form-hint">{newPlanData.description.length}/160 characters</div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date *</label>
                    <input 
                      type="date" 
                      value={newPlanData.startDate}
                      onChange={(e) => setNewPlanData({...newPlanData, startDate: e.target.value})}
                      required
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>End Date *</label>
                    <input 
                      type="date" 
                      value={newPlanData.endDate}
                      onChange={(e) => setNewPlanData({...newPlanData, endDate: e.target.value})}
                      required
                      className="form-input"
                    />
                  </div>
                </div>
                <div className="ingredient-modal-footer">
                  <button 
                    type="button" 
                    onClick={() => setShowCreatePlanForm(false)}
                    className="ingredient-clear-btn"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="ingredient-apply-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Creating...' : 'Create Plan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Meal Modal */}
      {showAddMealForm && (
        <div className="ingredient-modal-overlay">
          <div className="ingredient-modal-container">
            <div className="ingredient-modal-header">
              <h2 className="ingredient-modal-title">ADD MEAL</h2>
              <div className="ingredient-modal-subheader">
                <span className="quick-kitchen">MEAL PLANNING</span>
                <span className="modal-context-note">Plan your perfect meal</span>
              </div>
              <button 
                className="ingredient-modal-close" 
                onClick={() => {
                  setShowAddMealForm(false);
                  setRecipeSearchTerm('');
                  setSelectedRecipeForMeal(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="ingredient-modal-content">
              <form onSubmit={handleAddMeal}>
                <div className="recipe-meal-picker">
                  <div className="recipe-meal-picker-header">
                    <div>
                      <h3>Choose from recipes</h3>
                      <p>Use an existing recipe and see how it matches your pantry.</p>
                    </div>
                    {selectedRecipeForMeal && (
                      <button
                        type="button"
                        className="recipe-picker-clear"
                        onClick={() => {
                          setSelectedRecipeForMeal(null);
                          setRecipeSearchTerm('');
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    className="form-input recipe-picker-search"
                    value={recipeSearchTerm}
                    onChange={(e) => setRecipeSearchTerm(e.target.value)}
                    placeholder="Search recipes..."
                  />
                  <div className="recipe-picker-list">
                    {filteredRecipeChoices.length > 0 ? filteredRecipeChoices.map((recipe) => {
                      const pantrySummary = getRecipePantrySummary(recipe);
                      const isSelected = selectedRecipeForMeal?.id === recipe.id;
                      return (
                        <button
                          type="button"
                          key={recipe.id}
                          className={`recipe-picker-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => applyRecipeToMealForm(recipe)}
                        >
                          <span className="recipe-picker-title">{recipe.title}</span>
                          <span className="recipe-picker-meta">
                            {recipe.category || 'Recipe'} - {(Number(recipe.prepTime) || 0) + (Number(recipe.cookTime) || 0)} min
                          </span>
                          <span className="recipe-picker-pantry">
                            Pantry {pantrySummary.matched}/{pantrySummary.total || 0}
                          </span>
                        </button>
                      );
                    }) : (
                      <div className="recipe-picker-empty">No matching recipes found.</div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Meal Name *</label>
                  <input 
                    type="text" 
                    value={newMealData.mealName}
                    onChange={(e) => setNewMealData({...newMealData, mealName: e.target.value})}
                    required 
                    className="form-input"
                    placeholder="e.g., Grilled Salmon"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea 
                    value={newMealData.description}
                    onChange={(e) => setNewMealData({...newMealData, description: e.target.value.slice(0, 140)})}
                    className="form-textarea"
                    placeholder="Optional description..."
                    maxLength="140"
                    rows="3"
                  />
                  <div className="form-hint">{newMealData.description.length}/140 characters</div>
                </div>
                <div className="form-group">
                  <label>Meal Type (Optional)</label>
                  <select 
                    value={selectedMealType}
                    onChange={(e) => setSelectedMealType(e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select meal type (optional)</option>
                    <option value="breakfast">Breakfast</option>
                    <option value="lunch">Lunch</option>
                    <option value="dinner">Dinner</option>
                    <option value="snack">Snack</option>
                  </select>
                  {selectedMealType && (
                    <div className="form-hint">Pre-selected: {selectedMealType.charAt(0).toUpperCase() + selectedMealType.slice(1)}</div>
                  )}
                </div>
                <div className="form-group">
                  <label>Calories</label>
                  <input 
                    type="number" 
                    value={newMealData.calories}
                    onChange={(e) => setNewMealData({...newMealData, calories: e.target.value})}
                    min="0"
                    className="form-input"
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea 
                    value={newMealData.notes}
                    onChange={(e) => setNewMealData({...newMealData, notes: e.target.value})}
                    className="form-textarea"
                    placeholder="Optional notes..."
                    rows="3"
                  />
                </div>
                <div className="ingredient-modal-footer">
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowAddMealForm(false);
                      setRecipeSearchTerm('');
                      setSelectedRecipeForMeal(null);
                    }}
                    className="ingredient-clear-btn"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="ingredient-apply-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Adding...' : 'Add Meal'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && planToDelete && (
        <ModalPortal>
        <div className="modal-overlay">
          <div className="modal-content delete-confirm-modal">
            <div className="modal-header delete-header">
              <h3>🗑️ Delete Meal Plan</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowDeleteConfirm(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-content">
                <div className="delete-icon">⚠️</div>
                <div className="delete-text">
                  <h4>Are you sure you want to delete this meal plan?</h4>
                  <div className="plan-preview">
                    <div className="preview-item">
                      <span className="item-name">{planToDelete.name}</span>
                      <span className="item-details">
                        {planToDelete.meals ? planToDelete.meals.length : 0} meals
                      </span>
                    </div>
                  </div>
                  <p className="delete-warning">This action cannot be undone.</p>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn-secondary"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeletePlan}
                  className="btn-danger"
                  disabled={isLoading}
                >
                  {isLoading ? 'Deleting...' : 'Delete Plan'}
                </button>
              </div>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {/* Meal Details Modal */}
      {showMealDetails && selectedMeal && (
        <div className="modal-overlay">
          <div className="modal-content meal-details-modal">
            <div className="modal-header">
              <h3>🍽️ {selectedMeal.mealName}</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowMealDetails(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="meal-details-content">
                <div className="meal-info-grid">
                  <div className="info-item">
                    <span className="info-label">Meal Type:</span>
                    <span className="info-value">{selectedMeal.mealType.charAt(0).toUpperCase() + selectedMeal.mealType.slice(1)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Day:</span>
                    <span className="info-value">{selectedMeal.dayOfWeek.charAt(0).toUpperCase() + selectedMeal.dayOfWeek.slice(1)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Date:</span>
                    <span className="info-value">{new Date(selectedMeal.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Calories:</span>
                    <span className="info-value">{selectedMeal.calories || 'Not specified'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Status:</span>
                    <span className={`info-value ${selectedMeal.isCompleted ? 'completed' : 'pending'}`}>
                      {selectedMeal.isCompleted ? '✅ Completed' : '⏳ Pending'}
                    </span>
                  </div>
                </div>
                
                {selectedMeal.description && (
                  <div className="meal-description-section">
                    <h4>Description:</h4>
                    <p>{selectedMeal.description}</p>
                  </div>
                )}
                
                {selectedMeal.notes && (
                  <div className="meal-notes-section">
                    <h4>Notes:</h4>
                    <p>{selectedMeal.notes}</p>
                  </div>
                )}
                
                <div className="meal-actions">
                  <button 
                    className="btn-secondary"
                    onClick={() => setShowMealDetails(false)}
                  >
                    Close
                  </button>
                  <button 
                    className="btn-primary"
                    onClick={() => {
                      handleToggleMeal(selectedMeal.id, !selectedMeal.isCompleted);
                      setShowMealDetails(false);
                    }}
                  >
                    {selectedMeal.isCompleted ? 'Mark as Pending' : 'Mark as Completed'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Achievement-Style Success Notification */}
      {showSuccessMessage && (
        <div className="success-notification">
          <div className="success-content">
            <span className="success-icon">✅</span>
            <div className="success-text">
              <strong>Success!</strong>
              <span>{successMessage}</span>
            </div>
            <button className="success-close" onClick={() => setShowSuccessMessage(false)}>×</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealPlans;
